import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import { Box, Typography } from "@mui/material";
import type { ChannelMemberResponse } from "stream-chat";
import { ChatChannelName } from "Components/Chat/ChatChannelName";
import { ChatHeaderDirectAvatar } from "Components/Chat/ChatHeaderDirectAvatar";
import { ChatHeaderGroupAvatar } from "Components/Chat/ChatHeaderGroupAvatar";
import type { AccessRoleCode } from "Models/AccessRoleCodes";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  isGroup: boolean;
  isOnline: boolean;
  accessRole: AccessRoleCode | undefined;
  avatarImage: string | undefined;
  displayName: string;
  otherMembers: Array<ChannelMemberResponse>;
  /** Passed straight to the title; the header decides whether the counterpart may be linked. */
  profileUrl?: string;
  /** The group admits verified (Full Moone) users only (App ADR-0012) — shown under the name. */
  isFullMooneOnly?: boolean;
};

export const ChatChannelAvatarWithName = ({
  isGroup,
  isOnline,
  accessRole,
  avatarImage,
  displayName,
  otherMembers,
  profileUrl,
  isFullMooneOnly,
}: Props) => {
  const { t } = useResource();

  return (
    <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
      {isGroup ? (
        <ChatHeaderGroupAvatar otherMembers={otherMembers} />
      ) : (
        <ChatHeaderDirectAvatar
          avatarImage={avatarImage}
          displayName={displayName}
          accessRole={accessRole}
          isOnline={isOnline}
        />
      )}
      <Box flex={1} minWidth={0} overflow="hidden">
        <ChatChannelName displayName={displayName} profileUrl={profileUrl} />
        {isFullMooneOnly && (
          // The row itself is the Typography, so the colour covers the icon as well as the text.
          <Typography
            variant="caption"
            component="div"
            display="flex"
            alignItems="center"
            gap={0.5}
            minWidth={0}
            color="common.white"
            sx={{ opacity: 0.85 }}
          >
            <VerifiedUserOutlinedIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            {/* The ellipsis has to sit on the text node itself, not on the flex row around it. */}
            <Typography variant="caption" component="span" noWrap>
              {t(Resources.Chat.NewChat.FullMooneOnlyBadge)}
            </Typography>
          </Typography>
        )}
      </Box>
    </Box>
  );
};
